import { Avatar, Button, Checkbox, Popover, Tooltip } from 'antd'
import { UserAddOutlined } from '@ant-design/icons'
import { useMemo } from 'react'
import { getInitials, UNASSIGNED_CREW_ID } from './scheduleModel'
import { CrewMember } from './scheduleApi'
import './CrewFilter.css'

const MAX_VISIBLE_CREW = 4

type Props = {
  crew: CrewMember[] | undefined
  selectedCrewMemberIds: string[]
  onToggleCrewMember: (crewMemberId: string) => void
  onClearCrewFilter: () => void
}

const CrewMemberAvatar = ({ member, size }: { member: CrewMember, size: number }) => {
  const crewLeadColor = member.color || '#000000'
  const middleRing = Math.round(size * 0.85)
  const innerCircle = Math.round(size * 0.55)
  const fontSize = Math.round(size * 0.275)
  const borderWidth = size >= 40 ? 3 : 2

  if (member.avatar && !member.hasStockAvatar) {
    return <Avatar size={size} src={member.avatar} style={{ border: `${borderWidth}px solid ${crewLeadColor}`, backgroundColor: '#fff', flexShrink: 0 }} />
  }

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', backgroundColor: crewLeadColor,
      display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0,
    }}>
      <div style={{
        width: middleRing, height: middleRing, borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: innerCircle, height: innerCircle, borderRadius: '50%', backgroundColor: crewLeadColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize, fontWeight: 600, color: '#fff',
        }}>
          {getInitials(member.name)}
        </div>
      </div>
    </div>
  )
}

const CrewFilter = ({ crew, selectedCrewMemberIds, onToggleCrewMember, onClearCrewFilter }: Props) => {
  const hasActiveCrewFilter = selectedCrewMemberIds.length > 0

  const filteredCrew = useMemo(() => {
    return (crew ?? []).filter(member => !!member.userEmail)
  }, [crew])

  const sortedCrew = useMemo(() => {
    const selectedIds = new Set(selectedCrewMemberIds)
    return [...filteredCrew].sort((a, b) => {
      const aSelected = selectedIds.has(a.id.toString())
      const bSelected = selectedIds.has(b.id.toString())
      if (aSelected !== bSelected) {
        return aSelected ? -1 : 1
      }
      return a.name.localeCompare(b.name)
    })
  }, [filteredCrew, selectedCrewMemberIds])

  const alphabeticalCrew = useMemo(() => {
    return [...filteredCrew].sort((a, b) => a.name.localeCompare(b.name))
  }, [filteredCrew])

  const visibleCrew = sortedCrew.slice(0, MAX_VISIBLE_CREW)
  const overflowCrew = sortedCrew.slice(MAX_VISIBLE_CREW)
  const isUnassignedSelected = selectedCrewMemberIds.includes(UNASSIGNED_CREW_ID)

  return (
    <div style={{ display: 'flex', alignItems: 'center' }} className="schedule-title-bar__crew-controls" id="calendar-crew-filter">
      <Tooltip title="Unassigned">
        <div
          className="crew-filter__avatar-button"
          onClick={() => onToggleCrewMember(UNASSIGNED_CREW_ID)}
          style={{
            filter: hasActiveCrewFilter && !isUnassignedSelected ? 'grayscale(100%) brightness(1.4)' : 'none',
            zIndex: isUnassignedSelected ? 10 : 1,
          }}
        >
          <Avatar size={40} icon={<UserAddOutlined />} style={{ backgroundColor: '#FFF1F0', border: '2px dashed #820014', color: '#820014' }} />
        </div>
      </Tooltip>
      {visibleCrew.map((member) => {
        const isSelected = selectedCrewMemberIds.includes(member.id.toString())
        return (
          <Tooltip key={member.id} title={member.name}>
            <div
              className="crew-filter__avatar-button"
              onClick={() => onToggleCrewMember(member.id.toString())}
              style={{
                filter: hasActiveCrewFilter && !isSelected ? 'grayscale(100%) brightness(1.4)' : 'none',
                scale: hasActiveCrewFilter && isSelected ? '115%' : 'none',
                marginLeft: '-8px',
                zIndex: isSelected ? 10 : 1,
              }}
            >
              <CrewMemberAvatar member={member} size={40} />
            </div>
          </Tooltip>
        )
      })}
      {overflowCrew.length > 0 && (() => {
        const hasSelectedInOverflow = overflowCrew.some(m => selectedCrewMemberIds.includes(m.id.toString()))
        return (
          <Popover
            trigger="click"
            placement="bottom"
            arrow={false}
            content={
              <div style={{ margin: '-8px -12px' }}>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {alphabeticalCrew.map(member => {
                    const isSelected = selectedCrewMemberIds.includes(member.id.toString())
                    return (
                      <div
                        key={member.id}
                        onClick={() => onToggleCrewMember(member.id.toString())}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 12px',
                          cursor: 'pointer',
                          borderRadius: 6,
                          background: isSelected ? '#f0f5ff' : 'transparent',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) { e.currentTarget.style.background = '#f5f5f5' }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isSelected ? '#f0f5ff' : 'transparent'
                        }}
                      >
                        <Checkbox checked={isSelected} style={{ pointerEvents: 'none' }} />
                        <CrewMemberAvatar member={member} size={32} />
                        <span style={{ fontSize: 13 }}>{member.name}</span>
                      </div>
                    )
                  })}
                </div>
                <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 12px', display: 'flex', justifyContent: 'center' }}>
                  <Button
                    size="small"
                    disabled={!hasActiveCrewFilter}
                    onClick={onClearCrewFilter}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            }
          >
            <div
              className="crew-filter__avatar-button"
              style={{
                width: 40, height: 40,
                backgroundColor: hasSelectedInOverflow ? '#1890ff' : '#e8e8e8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginLeft: '-8px', zIndex: 2,
                fontSize: 13, fontWeight: 600,
                color: hasSelectedInOverflow ? '#fff' : '#595959',
                border: `2px solid ${hasSelectedInOverflow ? '#1890ff' : '#d9d9d9'}`,
              }}
            >
              +{overflowCrew.length}
            </div>
          </Popover>
        )
      })()}
    </div>
  )
}

export default CrewFilter
